import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {useIntl} from 'react-intl';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CircularProgress from '@mui/material/CircularProgress';
import FileUploadButton from './FileUploadButton';
import {DOC_STATUSES} from '../constants/documentCollection';

const useStyles = makeStyles()((theme) => ({
  card: {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    direction: 'ltr',
    textAlign: 'left',
  },
  cardAttached: {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.success.light,
  },
  cardApproved: {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.success.light,
  },
  cardRejected: {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.error.light,
  },
  cardInReview: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.lighter,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  titleSection: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    marginBottom: 4,
  },
  description: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
  },
  badgeAttached: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.main,
  },
  badgeRequired: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
  },
  badgeApproved: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.main,
  },
  badgeRejected: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.error.main,
  },
  badgeInReview: {
    backgroundColor: `${theme.palette.primary.main}1a`,
    color: theme.palette.primary.main,
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 12,
  },
  fileList: {
    marginTop: 12,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 6,
  },
  fileName: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  localFileName: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 2,
    color: theme.palette.error.main,
    display: 'flex',
    alignItems: 'center',
    '&:disabled': {
      opacity: 0.4,
      cursor: 'not-allowed',
    },
  },

  addAnother: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 8,
  },
  rejectionReason: {
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.error.main,
    fontWeight: 600,
    marginTop: 8,
    lineHeight: 1.5,
  },
  uploadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginTop: 12,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
  },
  batchSubmitButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    width: '100%',
    marginTop: 16,
    padding: '12px 24px',
    borderRadius: 25,
    border: 'none',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  batchError: {
    fontSize: theme.typography.pxToRem(13),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.error.main,
    textAlign: 'center',
    marginTop: 8,
  },
}));

const DocumentCard = ({
  document,
  uploadedFiles = [],
  onFileSelected,
  onRemoveFile,
  onBatchSubmit,
  uploading = false,
  batchError,
}) => {
  const {classes} = useStyles();
  const intl = useIntl();

  // When NOT_APPROVED — ignore backend files, treat as fresh upload
  const isRejected = document.status === DOC_STATUSES.NOT_APPROVED;
  const backendFiles = isRejected ? [] : document.files || [];
  const hasBackendFiles = backendFiles.length > 0;
  const hasLocalFiles = uploadedFiles.length > 0;
  const isMultiFile = document.multiFile;

  const getCardClass = () => {
    switch (document.status) {
    case DOC_STATUSES.APPROVED:
      return `${classes.card} ${classes.cardApproved}`;
    case DOC_STATUSES.NOT_APPROVED:
      return `${classes.card} ${classes.cardRejected}`;
    case DOC_STATUSES.IN_REVIEW:
    case DOC_STATUSES.ACCEPTED:
      return `${classes.card} ${classes.cardInReview}`;
    default:
      return classes.card;
    }
  };

  const renderStatusBadge = () => {
    switch (document.status) {
      case DOC_STATUSES.REQUIRED:
        return (
          <span className={`${classes.statusBadge} ${classes.badgeRequired}`}>
            {intl.formatMessage({id: 'docCollection.status.required'})}
          </span>
        );
    case DOC_STATUSES.APPROVED:
      return (
        <span className={`${classes.statusBadge} ${classes.badgeApproved}`}>
          <CheckCircleIcon style={{fontSize: 16}} />
          {intl.formatMessage({id: 'docCollection.status.approved'})}
        </span>
      );
    case DOC_STATUSES.NOT_APPROVED:
      return (
        <span className={`${classes.statusBadge} ${classes.badgeRejected}`}>
          <ErrorIcon style={{fontSize: 16}} />
          {intl.formatMessage({id: 'docCollection.status.notApproved'})}
        </span>
      );
    case DOC_STATUSES.IN_REVIEW:
      return (
        <span className={`${classes.statusBadge} ${classes.badgeInReview}`}>
          <ScheduleIcon style={{fontSize: 16}} />
          {intl.formatMessage({id: 'docCollection.status.inReview'})}
        </span>
      );
    case DOC_STATUSES.ACCEPTED:
      return (
        <span className={`${classes.statusBadge} ${classes.badgeInReview}`}>
          <ScheduleIcon style={{fontSize: 16}} />
          {intl.formatMessage({id: 'docCollection.status.accepted'})}
        </span>
      );
    default:
      return null;
    }
  };

  // Backend files — read-only with ✓ icon
  const renderBackendFiles = () => {
    if (!hasBackendFiles) return null;
    return (
      <div className={classes.fileList}>
        {backendFiles.map((filename, index) => (
          <div className={classes.fileRow} key={`backend-${index}`}>
            <span className={classes.fileName}>📎 {filename}</span>
          </div>
        ))}
      </div>
    );
  };

  // Local files — with delete button (multi-file mode only)
  const renderLocalFiles = () => {
    if (!hasLocalFiles) return null;
    return (
      <div className={classes.fileList}>
        {uploadedFiles.map((fileData, index) => (
          <div className={classes.fileRow} key={`local-${index}`}>
            <span className={classes.localFileName}>
              {isMultiFile ? '📎' : '📎'} {fileData.file?.name}
            </span>
            {isMultiFile && onRemoveFile && (
              <button
                type='button'
                className={classes.deleteButton}
                onClick={() => onRemoveFile(document.id, index)}
                disabled={uploading}
              >
                <DeleteOutlineIcon style={{fontSize: 18}} />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Determine whether to show the upload button
  const shouldShowUpload = () => {
    if (document.status === DOC_STATUSES.APPROVED) return false;

    if (isMultiFile) {
      // Multi-file: always allow adding more unless approved
      return document.status !== DOC_STATUSES.APPROVED;
    }

    // Single-file: show upload only if no files exist yet, or if NOT_APPROVED (re-upload)
    if (document.status === DOC_STATUSES.NOT_APPROVED) return true;
    if (hasBackendFiles || hasLocalFiles) return false;
    return true;
  };

  const showUpload = shouldShowUpload();
  const isFirstUpload = !hasBackendFiles && !hasLocalFiles;

  return (
    <div className={getCardClass()}>
      <div className={classes.header}>
        <div className={classes.titleSection}>
          <div className={classes.title}>{document.label}</div>
          <div className={classes.description}>{document.description}</div>
        </div>
        {renderStatusBadge()}
      </div>

      {document.status === DOC_STATUSES.NOT_APPROVED && document.rejectionReason && (
        <div className={classes.rejectionReason}>{document.rejectionReason}</div>
      )}

      {uploading && (
        <div className={classes.uploadingRow}>
          <CircularProgress size={20} />
          <span>{intl.formatMessage({id: 'docCollection.upload.uploading'})}</span>
        </div>
      )}

      {renderBackendFiles()}
      {renderLocalFiles()}

      {/* Single-file mode: upload button (first upload or re-upload on NOT_APPROVED) */}
      {showUpload && !isMultiFile && (
        <div className={classes.actions}>
          <FileUploadButton
            onFileSelected={(file) => onFileSelected(document.id, file)}
            label={document.status === DOC_STATUSES.NOT_APPROVED
              ? intl.formatMessage({id: 'docCollection.fileUpload.reuploadLabel'})
              : intl.formatMessage({id: 'docCollection.fileUpload.label'})}
          />
        </div>
      )}

      {/* Multi-file mode: add more link */}
      {showUpload && isMultiFile && (
        <div className={classes.addAnother}>
          <FileUploadButton
            onFileSelected={(file) => onFileSelected(document.id, file)}
            label={isFirstUpload
              ? intl.formatMessage({id: 'docCollection.fileUpload.label'})
              : intl.formatMessage({id: 'docCollection.fileUpload.addAnotherForRange'})}
            variant={isFirstUpload ? 'outlined' : 'text'}
          />
        </div>
      )}

      {/* Multi-file mode: batch submit button — only when there are local files */}
      {isMultiFile && hasLocalFiles && (
        <>
          <button
            type='button'
            className={classes.batchSubmitButton}
            onClick={() => onBatchSubmit && onBatchSubmit(document.id)}
            disabled={uploading}
          >
            {intl.formatMessage({id: 'docCollection.fileUpload.submitBatch'})}
          </button>
          {batchError && (
            <div className={classes.batchError}>{batchError}</div>
          )}
        </>
      )}
    </div>
  );
};

DocumentCard.propTypes = {
  batchError: PropTypes.string,
  document: PropTypes.shape({
    description: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.string),
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    multiFile: PropTypes.bool,
    rejectionReason: PropTypes.string,
    status: PropTypes.string,
    type: PropTypes.string.isRequired,
  }).isRequired,
  onBatchSubmit: PropTypes.func,
  onFileSelected: PropTypes.func,
  onRemoveFile: PropTypes.func,
  uploading: PropTypes.bool,
  uploadedFiles: PropTypes.arrayOf(PropTypes.shape({
    file: PropTypes.object,
  })),
};

export default DocumentCard;
